import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { offersAPI } from '../../services/api';
import Header from '../../components/layout/Header';
import { MapPin, Phone, Clock, Share2 } from 'lucide-react';

export default function OfferDetail() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading } = useQuery({
    queryKey: ['offer', id],
    queryFn: () => offersAPI.getById(parseInt(id!)).then((res) => res.data),
    enabled: !!id,
  });

  const handleShare = () => {
    if (navigator.share && data?.data) {
      navigator.share({
        title: data.data.offer.title,
        text: `Check out this deal: ${data.data.offer.title}`,
        url: window.location.href,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow animate-pulse h-96" />
        </div>
      </div>
    );
  }

  if (!data?.data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <p className="text-gray-600">Offer not found</p>
        </div>
      </div>
    );
  }

  const { offer, restaurant } = data.data;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-4 text-sm text-gray-600">
          <Link to="/" className="hover:text-red-600">Home</Link>
          {' / '}
          <Link to="/offers" className="hover:text-red-600">Offers</Link>
          {' / '}
          <span className="text-gray-900">{offer.title}</span>
        </nav>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Image */}
          <div className="relative h-96 bg-gray-200">
            {offer.imageUrl ? (
              <img
                src={offer.imageUrl}
                alt={offer.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                No Image Available
              </div>
            )}
            {offer.discountPercentage && (
              <div className="absolute top-6 right-6 bg-red-600 text-white px-6 py-3 rounded-full font-bold text-2xl">
                {offer.discountPercentage}% OFF
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Restaurant Name */}
            <Link
              to={`/restaurants/${restaurant?.id}`}
              className="text-red-600 hover:text-red-700 font-medium mb-2 inline-block"
            >
              {restaurant?.name}
            </Link>

            {/* Title */}
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{offer.title}</h1>

            {/* Price */}
            <div className="flex items-center gap-4 mb-6">
              {offer.offerPrice && (
                <span className="text-4xl font-bold text-green-600">₹{offer.offerPrice}</span>
              )}
              {offer.originalPrice && (
                <span className="text-2xl text-gray-400 line-through">
                  ₹{offer.originalPrice}
                </span>
              )}
            </div>

            {/* Description */}
            <p className="text-lg text-gray-700 mb-6">{offer.description}</p>

            {/* Validity */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-yellow-800">
                <Clock className="w-5 h-5" />
                <span className="font-medium">
                  Valid from {new Date(offer.validFrom).toLocaleDateString()} to{' '}
                  {new Date(offer.validTo).toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Terms & Conditions */}
            {offer.termsConditions && (
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Terms & Conditions</h2>
                <p className="text-gray-700 whitespace-pre-line">{offer.termsConditions}</p>
              </div>
            )}

            {/* Restaurant Info */}
            <div className="border-t pt-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Restaurant Details</h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-gray-900">{restaurant?.address}</p>
                    <p className="text-gray-600">{restaurant?.area}, Udaipur</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <a
                    href={`tel:${restaurant?.phone}`}
                    className="text-red-600 hover:text-red-700 font-medium"
                  >
                    {restaurant?.phone}
                  </a>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <a
                href={`tel:${restaurant?.phone}`}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition text-center"
              >
                Call Restaurant
              </a>
              <button
                onClick={handleShare}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
